import { z } from "zod";
import { GeoJsonLineString, IsoDateTime, OpaqueId } from "./common.js";
export const TransitMode = z.enum([
    "metro",
    "tram",
    "bus",
    "brt", // TEOR / FAST
    "ferry",
    "other",
]);
export const Line = z.object({
    id: OpaqueId,
    code: z.string(), // code affiché : « T1 », « F2 », « M »
    name: z.string(),
    mode: TransitMode,
    color: z.string(), // hex, branding de ligne — vient de la config réseau, jamais codé en dur
    textColor: z.string(),
    operatorId: z.string(),
    group: z.string().nullable(),
    // D-014 — logo SVG de marque de la ligne, servi par l'API
    // (`GET /networks/{n}/assets/line-icons/{ref}.svg`). `null` ⇒ aucun asset pour
    // ce `route_short_name` : le front garde le badge CSS `color`/`textColor`.
    logoUrl: z.string().nullable(),
});
export const LinesResponse = z.object({ lines: z.array(Line) });
export const LineShapeResponse = z.object({
    lineId: OpaqueId,
    direction: z.union([z.literal(0), z.literal(1)]),
    shape: GeoJsonLineString,
    // D-019 — provenance de la géométrie : `pfaedle` (map-matching GTFS→OSM),
    // `osm-relation` (relation `type=route` OSM curée), `gtfs` (shapes.txt brut,
    // souvent « vol d'oiseau »), `manual` (GeoJSON déposé en admin). Le front
    // affiche un discret badge + la mention ODbL quand la source dérive d'OSM.
    source: z.enum(["pfaedle", "osm-relation", "gtfs", "manual"]),
});
/** Arrêt ordonné le long d'une ligne — base du thermomètre et de l'espace-temps (CDC §5.3). */
export const LineStop = z.object({
    stopId: OpaqueId,
    name: z.string(),
    sequence: z.number().int(),
    distanceM: z.number().nonnegative(), // distance cumulée le long du tracé
});
export const LineStopsResponse = z.object({
    lineId: OpaqueId,
    direction: z.union([z.literal(0), z.literal(1)]),
    stops: z.array(LineStop),
});
export const LineHealth = z.object({
    lineId: OpaqueId,
    lineCode: z.string(),
    status: z.enum(["ok", "degraded", "down"]),
    scheduleFallback: z.boolean(), // ligne retombée à l'horaire théorique (CDC §2.3)
    since: IsoDateTime.nullable(),
    openAlerts: z.number().int().nonnegative(),
});
export const LinesHealthResponse = z.object({ lines: z.array(LineHealth) });
/**
 * Couverture de service d'une ligne / sens à l'instant courant (D-018).
 * Sert la surcouche carte « Fréquence » : distinguer un trou de desserte
 * « −1 sortie » (structurel, `activeRuns < plannedRuns`) d'un simple retard.
 * Recalculé à chaque cycle du moteur (`line_headways`).
 */
export const LineCoverage = z.object({
    lineId: OpaqueId,
    lineCode: z.string(),
    direction: z.union([z.literal(0), z.literal(1)]),
    plannedRuns: z.number().int().nonnegative(), // sorties prévues au tableau de marche à cet instant
    activeRuns: z.number().int().nonnegative(), // sorties effectivement en circulation (position ≠ « scheduled »)
    targetHeadwaySeconds: z.number().int().positive().nullable(), // fréquence cible ; null si < 2 courses
});
export const LineCoverageResponse = z.object({ lines: z.array(LineCoverage) });
/** Arrêt pour la couche carte (`GET /networks/{n}/stops?bbox=`). */
export const MapStop = z.object({
    id: OpaqueId,
    name: z.string(),
    lat: z.number().nullable(),
    lon: z.number().nullable(),
    isStation: z.boolean(), // location_type = 1 (regroupement de quais)
    parentStation: OpaqueId.nullable(),
    timezone: z.string().nullable(), // fuseau propre à l'arrêt (CDC §3.2)
    // D-017 — lignes desservant l'arrêt (GTFS `stop_times` → `trips` → `routes`),
    // triées. `[]` si aucune (arrêt hors service ce jeu de données).
    lineIds: z.array(OpaqueId),
    // D-017 — couleur de branding de la 1re ligne (par `route_sort_order`),
    // résolue serveur (`#RRGGBB`). Le front peint le cercle d'arrêt avec.
    // `null` si aucune ligne ou aucune couleur GTFS.
    color: z.string().nullable(),
});
export const StopsResponse = z.object({ stops: z.array(MapStop) });
//# sourceMappingURL=lines.js.map